import '@mdi/font/css/materialdesignicons.min.css'
import { createApp, mergeProps } from 'vue'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import store from './store'
import { createI18n } from 'vue-i18n'
// @ts-ignore
import messages from './messages'
import { createRouter, createWebHistory } from 'vue-router'
import LoginMenu from './components/LoginMenu.vue'
import ModelMenu from './components/main/ModelMenu.vue'
import ModelLogo from './components/main/ModelLogo.vue'
import NotFound from './components/NotFound.vue'
import BearerToken from './components/BearerToken.vue'
import Main from './components/main/Main.vue'
import TopLeftMenu from './components/main/TopLeftMenu.vue'
import { installRetryInterceptor } from '@/plugins/axios-retry'
import { confirm } from '@/plugins/confirm'
import { msalPluginInstance } from '@/plugins/msal'

const AdminMain      = () => import(/* webpackChunkName: "admin" */          '@/components/admin/Main.vue');
const AddLogin       = () => import(/* webpackChunkName: "admin-logins" */   '@/components/admin/AddLogin.vue');
const Logins         = () => import(/* webpackChunkName: "admin-logins" */   '@/components/admin/Logins.vue');
const ScenariosList  = () => import(/* webpackChunkName: "admin-scenarios" */'@/components/admin/ScenariosList.vue');
const Models         = () => import(/* webpackChunkName: "admin-models" */   '@/components/admin/Models.vue');
const ModelDialogs   = () => import(/* webpackChunkName: "admin-models" */   '@/components/admin/ModelDialogs.vue');
const Groups         = () => import(/* webpackChunkName: "admin-groups" */   '@/components/admin/Groups.vue');
const AddGroup       = () => import(/* webpackChunkName: "admin-groups" */   '@/components/admin/AddGroup.vue');
const LogPage        = () => import(/* webpackChunkName: "admin-log" */      '@/components/admin/LogPage.vue');
const UsagePage      = () => import(/* webpackChunkName: "admin-usage" */    '@/components/admin/UsagePage.vue');
const Roles          = () => import(/* webpackChunkName: "admin-roles" */    '@/components/admin/Roles.vue');
const RoleDialog     = () => import(/* webpackChunkName: "admin-roles" */    '@/components/admin/RoleDialog.vue');
const UserAccess     = () => import(/* webpackChunkName: "admin-useraccess" */'@/components/admin/UserAccess.vue');
const ApiKeys        = () => import(/* webpackChunkName: "admin-apikeys" */  '@/components/admin/ApiKeys.vue');
const ApiKeyDialog   = () => import(/* webpackChunkName: "admin-apikeys" */  '@/components/admin/ApiKeyDialog.vue');

import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, RadialLinearScale, LineElement, LineController } from 'chart.js'
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, RadialLinearScale, LineElement, LineController)
import ChartDataLabels from 'chartjs-plugin-datalabels';
ChartJS.register(ChartDataLabels);

// vue-i18n 9 in legacy mode keeps the Options API ($t / $tc / $i18n) working
// with the existing message files, minimising churn across components.
const i18n = createI18n({
    legacy: true,
    locale: 'en',
    messages,
})

const defaultRoute = {
    path: '/',
    name: 'Default',
    components: {
        default: Main,
        loginMenu: LoginMenu,
        modelMenu: ModelMenu,
        modelLogo: ModelLogo,
        topLeftMenu: TopLeftMenu,
    }
}

const defaultRoute2 = {
    path: '/test',
    name: 'Test',
    components: {
        loginMenu: LoginMenu,
        topLeftMenu: TopLeftMenu,
    }
}

// vue-router 4: named views for a single route must live at the same depth.
// The admin sections render into AdminMain's adminLeft/adminRight <router-view>s,
// so they are child routes of /admin (AdminMain is the parent/layout). The app-bar
// views (loginMenu/topLeftMenu) stay on the parent.
const adminRoute = {
    path: '/admin',
    components: {
        default: AdminMain,
        loginMenu: LoginMenu,
        topLeftMenu: TopLeftMenu
    },
    children: [
        { path: '', name: 'Admin' },
        { path: 'log', name: 'AdminLog', components: { adminLeft: LogPage } },
        { path: 'usage', name: 'AdminUsage', components: { adminLeft: UsagePage } },
        { path: 'logins', name: 'AdminLogins', components: { adminLeft: Logins, adminRight: AddLogin } },
        { path: 'models', name: 'AdminModels', components: { adminLeft: Models, adminRight: ModelDialogs } },
        { path: 'groups', name: 'AdminGroups', components: { adminLeft: Groups, adminRight: AddGroup } },
        { path: 'roles', name: 'AdminRoles', components: { adminLeft: Roles, adminRight: RoleDialog } },
        { path: 'scenarioStatus', name: 'ScenarioStatus', components: { adminLeft: ScenariosList } },
        { path: 'user-access', name: 'AdminUserAccess', components: { adminLeft: UserAccess } },
        { path: 'apikeys', name: 'AdminApiKeys', components: { adminLeft: ApiKeys, adminRight: ApiKeyDialog } },
    ]
}

// vue-router 4: the catch-all `path: '*'` is replaced by a param matcher.
const notFoundRoute = {
    path: '/:pathMatch(.*)*',
    components: {
        default: NotFound,
    }
}

export const router = createRouter({
    history: createWebHistory(),
    routes: [
        defaultRoute, defaultRoute2, adminRoute, notFoundRoute
    ]
})

router.beforeEach((to, from, next) => {
    if (to.path.startsWith('/admin')) {
        if (from.name === 'Default') {
            sessionStorage.setItem('lastHomeQuery', JSON.stringify(from.query));
        }
        store.commit('main/SET_MODEL', null);
        document.title = 'IRIS Platform';
    }
    next();
})

// Global filters are removed in Vue 3. Expose the former filter functions as
// `$filters` on globalProperties; templates change from `x | fixed(4)` to
// `$filters.fixed(x, 4)`.
import dateFilter from '@/filters/date.filter';
import timeFilter from '@/filters/time.filter';
import datetimeFilter from '@/filters/datetime.filter';
import currencyFilter from '@/filters/currency.filter';
import roundingFilter from '@/filters/rounding.filter';
import fixedNumberFilter from '@/filters/fixedNumber.filter';
import percentageFilter from '@/filters/percentage.filter';

const filters = {
    date: dateFilter,
    time: timeFilter,
    datetime: datetimeFilter,
    currency: currencyFilter,
    percentage: percentageFilter,
    rounding: roundingFilter,
    fixed: fixedNumberFilter,
};

installRetryInterceptor(store, i18n.global)

const app = createApp(App)
app.config.globalProperties.Window = window
app.config.globalProperties.$filters = filters
// Vue's mergeProps, for combining nested menu+tooltip activator props in templates
// (object spread drops the internal activator ref; mergeProps preserves it).
app.config.globalProperties.mergeProps = mergeProps
app.config.globalProperties.$confirm = confirm
// $msal is created lazily (LoginMenu fetches its config); expose the live
// singleton via a getter so components keep using this.$msal.
Object.defineProperty(app.config.globalProperties, '$msal', {
    get: () => msalPluginInstance,
})

app.use(router)
app.use(i18n)
app.use(vuetify)
app.use(store)
app.mount('#app')

// vue-i18n 9: the translate API for use outside components lives on `.global`.
// Exporting it keeps existing `import i18n from '@/main'; i18n.t(...)` call sites working.
export default i18n.global;
